import { Grid, TextField, Typography } from "@mui/material";
import { SupplierSelector, WarehouseSelector } from "domains/legacy-integration/components";
import { useCallback, useEffect, useState } from "react";

import { PartitionSelector } from "domains/partitions/components";
import { SelectedPartitionDetails } from "domains/partitions/components/partition-selector/PartitionSelector";
import { SelectedSupplierDetails } from "domains/legacy-integration/components/supplier-selector/SupplierSelector";
import { SelectedWarehouseDetails } from "domains/legacy-integration/components/warehouse-selector/WarehouseSelector";
import localisations from "../../../localisation";

type EnterDetailsStageProps = {
    fieldCompanyId: number;
    fields: {
        partitionDetails: SelectedPartitionDetails;
        supplierDetails: SelectedSupplierDetails;
        warehouseDetails: SelectedWarehouseDetails;
        requiredDate: string;
    };
    handleFieldChange: (fields: any) => void;
    onDisableNextButton?: (disableNextButton: boolean) => void;
};

function EnterDetailsStage(props: EnterDetailsStageProps) {
    const { heading, blurb, requiredDateLabel } = localisations.createPreAlert.stages.enterDetails;
    const { fields, handleFieldChange, onDisableNextButton } = props;
    const validatedRequiredDate = (requiredDate: string): boolean => {
        return requiredDate !== undefined && requiredDate !== "";
    };

    const [partitionSelectorIsValid, setPartitionSelectorIsValid] = useState<boolean>(false);
    const [supplierSelectorIsValid, setSupplierSelectorIsValid] = useState<boolean>(false);
    const [warehouseSelectorIsValid, setWarehouseSelectorIsValid] = useState<boolean>(false);
    const [requiredDateIsValid, setRequiredDateIsValid] = useState(false);

    useEffect(() => {
        if (fields.requiredDate === undefined) {
            handleFieldChange({ requiredDate: new Date().toISOString().slice(0, 10) });
        }

        setRequiredDateIsValid(validatedRequiredDate(fields.requiredDate));
    }, [fields.requiredDate, handleFieldChange]);

    useEffect(() => {
        onDisableNextButton?.(
            !requiredDateIsValid || !partitionSelectorIsValid || !supplierSelectorIsValid || !warehouseSelectorIsValid
        );
    }, [
        requiredDateIsValid,
        partitionSelectorIsValid,
        supplierSelectorIsValid,
        warehouseSelectorIsValid,
        onDisableNextButton,
    ]);

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {heading}
            </Typography>

            <Typography variant="body1" gutterBottom>
                {blurb}
            </Typography>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <PartitionSelector
                        id="partition-selector"
                        fieldCompanyId={props.fieldCompanyId}
                        initialPartitionId={fields.partitionDetails?.id}
                        onSelected={useCallback(
                            (partitionDetails) => handleFieldChange({ partitionDetails }),
                            [handleFieldChange]
                        )}
                        required={true}
                        isValid={setPartitionSelectorIsValid}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SupplierSelector
                        id="supplier-selector"
                        fieldCompanyId={props.fieldCompanyId}
                        initialSupplierId={fields.supplierDetails?.id}
                        onSelected={useCallback((supplierDetails) => handleFieldChange({ supplierDetails }), [handleFieldChange])}
                        required={true}
                        isValid={setSupplierSelectorIsValid}
                    />
                </Grid>
                <Grid item xs={12}>
                    <WarehouseSelector
                        id="warehouse-selector"
                        fieldCompanyId={props.fieldCompanyId}
                        initialWarehouseId={fields.warehouseDetails?.id}
                        onSelected={useCallback(
                            (warehouseDetails) => handleFieldChange({ warehouseDetails }),
                            [handleFieldChange]
                        )}
                        required={true}
                        isValid={setWarehouseSelectorIsValid}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        id="pre-alert-required-date-field"
                        label={requiredDateLabel}
                        required
                        InputProps={{
                            "aria-label": requiredDateLabel,
                        }}
                        error={!requiredDateIsValid}
                        fullWidth
                        type="date"
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={fields.requiredDate ?? ""}
                        onChange={(event) => {
                            handleFieldChange({ requiredDate: event.target.value });
                        }}
                    />
                </Grid>
            </Grid>
        </>
    );
}

export default EnterDetailsStage;
